import * as React from 'react';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import {
  assignTournamentCompetitorNumbers,
  getTournamentCompetitors,
  getTournamentMeetPack,
  updateTournamentCompetitorNumbers
} from 'Src/helpers/service/adminSU/tournamentsTable';
import './TournamentCompetitorsSummary.scss';

interface Props { user: AppUser; tournamentId: string; onDownloadComplete?: () => void; }

interface Competitor {
  id: string;
  teamId: string;
  userId?: string;
  permissionId?: string;
  firstName: string;
  lastName: string;
  schoolName: string;
  gender?: string;
  birthday?: string;
  form?: { age?: number | string; name?: string };
  quantumPlayerNumber?: number | string;
  events?: any[];
}

const competitorKey = (item: Competitor) => `${item.userId || item.id}:${item.permissionId || ''}`;
const valueKey = (value: any) => String(value == null ? '' : value);
const displayGender = (gender?: string) => ({ MALE: 'Male', FEMALE: 'Female', MIXED: 'Mixed' }[String(gender || '').toUpperCase()] || gender || '—');
const displayAge = (item: Competitor) => item.form && (item.form.age != null || item.form.name)
  ? String(item.form.age != null ? item.form.age : item.form.name)
  : '—';
const eventName = (event: any) => String(
  event.shortDescription ||
  (event.generatedNames && event.generatedNames.official) ||
  event.name ||
  'Event'
);

const mergeCompetitors = (items: Competitor[]): Competitor[] => {
  const competitors: { [key: string]: Competitor } = {};
  items.forEach(item => {
    const key = competitorKey(item);
    if (!competitors[key]) competitors[key] = { ...item, events: [] };
    const eventIds = new Set((competitors[key].events || []).map(event => String(event.id)));
    (item.events || []).forEach(event => {
      if (!eventIds.has(String(event.id))) {
        competitors[key].events!.push(event);
        eventIds.add(String(event.id));
      }
    });
  });
  return Object.keys(competitors).map(key => competitors[key]).sort((left, right) =>
    String(left.lastName || '').localeCompare(String(right.lastName || ''), undefined, { sensitivity: 'base' }) ||
    String(left.firstName || '').localeCompare(String(right.firstName || ''), undefined, { sensitivity: 'base' })
  );
};

const optionalColumns = [
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Gender' },
  { key: 'birthday', label: 'Date of birth' },
  { key: 'events', label: 'Events' }
];

export class TournamentCompetitorsSummary extends React.Component<Props, any> {
  state = {
    items: [] as Competitor[], loading: true, saving: false, error: '', notice: '', startFrom: 1, search: '',
    school: '', age: '', gender: '', columnsOpen: false, filtersOpen: false,
    visibleColumns: { age: true, gender: true, birthday: true, events: true },
    originalNumbers: {} as Record<string, string>
  };

  componentDidMount() { this.load(); }

  load = () => {
    this.setState({ loading: true, error: '', notice: '' });
    return getTournamentCompetitors(this.props.user, this.props.tournamentId)
      .then(rawItems => {
        const items = mergeCompetitors(rawItems);
        const originalNumbers = items.reduce((result, item) => ({ ...result, [competitorKey(item)]: valueKey(item.quantumPlayerNumber) }), {});
        this.setState({ items, originalNumbers, loading: false });
      })
      .catch(error => this.readError(error).then(message => this.setState({ error: message, loading: false })));
  };

  readError = async error => {
    let data = error && error.response && error.response.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try { data = JSON.parse(await data.text()); } catch (e) { return 'The request could not be completed.'; }
    }
    return data && data.details && data.details.text ? data.details.text : 'The request could not be completed.';
  };

  changeNumber = (key: string, value: string) => this.setState({
    items: this.state.items.map(item => competitorKey(item) === key ? { ...item, quantumPlayerNumber: value } : item),
    notice: ''
  });

  dirtyItems = () => this.state.items.filter(item =>
    valueKey(item.quantumPlayerNumber) !== valueKey(this.state.originalNumbers[competitorKey(item)])
  );

  saveNumbers = () => {
    const dirtyItems = this.dirtyItems();
    const updates = dirtyItems.map(item => ({
      teamId: item.teamId,
      playerId: item.id,
      quantumPlayerNumber: Number(item.quantumPlayerNumber)
    }));
    if (!updates.length) return;
    if (updates.some(item => !Number.isInteger(item.quantumPlayerNumber) || item.quantumPlayerNumber < 1)) {
      return this.setState({ error: 'Every changed competitor number must be a positive whole number.' });
    }
    const numbers = updates.map(item => item.quantumPlayerNumber);
    if (new Set(numbers).size !== numbers.length) return this.setState({ error: 'Changed competitor numbers must be unique.' });
    this.setState({ saving: true, error: '', notice: '' });
    return updateTournamentCompetitorNumbers(this.props.user, this.props.tournamentId, updates)
      .then(result => this.setState({
        saving: false,
        originalNumbers: this.state.items.reduce((values, item) => ({ ...values, [competitorKey(item)]: valueKey(item.quantumPlayerNumber) }), {}),
        notice: `${result.updated} ${result.updated === 1 ? 'competitor number' : 'competitor numbers'} saved.`
      }))
      .catch(error => this.readError(error).then(message => this.setState({ saving: false, error: message })));
  };

  discardNumbers = () => this.setState({
    items: this.state.items.map(item => ({ ...item, quantumPlayerNumber: this.state.originalNumbers[competitorKey(item)] })),
    error: '', notice: ''
  });

  assignAll = () => {
    const startFrom = Number(this.state.startFrom);
    if (!Number.isInteger(startFrom) || startFrom < 1) return this.setState({ error: 'Starting number must be a positive whole number.' });
    this.setState({ loading: true, error: '', notice: '' });
    return assignTournamentCompetitorNumbers(this.props.user, this.props.tournamentId, startFrom)
      .then(this.load)
      .catch(error => this.readError(error).then(message => this.setState({ loading: false, error: message })));
  };

  downloadMeetPack = () => {
    this.setState({ loading: true, error: '' });
    return getTournamentMeetPack(this.props.user, this.props.tournamentId).then(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = 'meetPack.zip'; document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url); this.setState({ loading: false });
      if (this.props.onDownloadComplete) this.props.onDownloadComplete();
    }).catch(error => this.readError(error).then(message => this.setState({ loading: false, error: message })));
  };

  render() {
    const { items, loading, saving, error, notice, startFrom, search, school, age, gender, visibleColumns, columnsOpen } = this.state;
    const query = search.trim().toLowerCase();
    const schools = Array.from(new Set(items.map(item => item.schoolName).filter(Boolean))).sort((a: string, b: string) => a.localeCompare(b));
    const ages = Array.from(new Set(items.map(displayAge).filter(value => value !== '—'))).sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }));
    const genders = Array.from(new Set(items.map(item => item.gender).filter(Boolean))).sort();
    const visibleItems = items.filter(item =>
      (!query || [item.firstName, item.lastName, item.schoolName].concat((item.events || []).map(eventName)).filter(Boolean).join(' ').toLowerCase().includes(query)) &&
      (!school || item.schoolName === school) && (!age || displayAge(item) === age) && (!gender || item.gender === gender)
    );
    const dirtyCount = this.dirtyItems().length;
    const activeFilterCount = [school, age, gender].filter(Boolean).length;

    return <section className="tournamentCompetitors">
      <header className="tournamentCompetitors__header">
        <div><h3>Competitors</h3><p>{items.length} athletes · review entries, events and competitor numbers.</p></div>
        <button type="button" className="tournamentCompetitors__download" onClick={this.downloadMeetPack} disabled={loading}>Download Meet Pack</button>
      </header>
      <div className="tournamentCompetitors__tools">
        <div className="tournamentCompetitors__searchWrap"><span aria-hidden="true">⌕</span><input className="tournamentCompetitors__search" type="search" value={search} placeholder="Search competitor, school or event" onChange={event => this.setState({ search: event.target.value })} /></div>
        <div className="tournamentCompetitors__toolActions">
          <button type="button" className={activeFilterCount ? 'is-active' : ''} onClick={() => this.setState({ filtersOpen: !this.state.filtersOpen })}>Filters{activeFilterCount ? ` · ${activeFilterCount}` : ''}</button>
          <div className="tournamentCompetitors__columns"><button type="button" className={columnsOpen ? 'is-active' : ''} onClick={() => this.setState({ columnsOpen: !columnsOpen })}>Columns</button>{columnsOpen && <div className="tournamentCompetitors__popover"><strong>Visible columns</strong>{optionalColumns.map(column => <label key={column.key}><input type="checkbox" checked={visibleColumns[column.key]} onChange={() => this.setState({ visibleColumns: { ...visibleColumns, [column.key]: !visibleColumns[column.key] } })} /><span>{column.label}</span></label>)}</div>}</div>
        </div>
      </div>
      {this.state.filtersOpen && <div className="tournamentCompetitors__filters">
        <label><span>School</span><select value={school} onChange={event => this.setState({ school: event.target.value })}><option value="">All schools</option>{schools.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
        <label><span>Age</span><select value={age} onChange={event => this.setState({ age: event.target.value })}><option value="">All ages</option>{ages.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
        <label><span>Gender</span><select value={gender} onChange={event => this.setState({ gender: event.target.value })}><option value="">All genders</option>{genders.map(value => <option key={value} value={value}>{displayGender(value)}</option>)}</select></label>
        <button type="button" onClick={() => this.setState({ school: '', age: '', gender: '' })} disabled={!activeFilterCount}>Clear filters</button>
      </div>}
      <div className="tournamentCompetitors__numberBar">
        <div className="tournamentCompetitors__assign"><label><span>Auto-number from</span><input type="number" min="1" value={startFrom} onChange={event => this.setState({ startFrom: event.target.value })} /></label><button type="button" onClick={this.assignAll} disabled={loading || saving}>Assign numbers</button></div>
        <div className="tournamentCompetitors__batch"><span>{dirtyCount ? `${dirtyCount} unsaved ${dirtyCount === 1 ? 'change' : 'changes'}` : 'Numbers are up to date'}</span>{dirtyCount > 0 && <button type="button" onClick={this.discardNumbers} disabled={saving}>Discard</button>}<button type="button" className="is-primary" onClick={this.saveNumbers} disabled={!dirtyCount || saving}>{saving ? 'Saving…' : `Save ${dirtyCount || ''} changes`}</button></div>
      </div>
      {error && <div className="tournamentCompetitors__error">{error}</div>}
      {notice && <div className="tournamentCompetitors__notice">✓ {notice}</div>}
      {loading ? <div className="tournamentCompetitors__empty">Loading competitors…</div> : visibleItems.length === 0 ?
        <div className="tournamentCompetitors__empty">{items.length ? 'No competitors match these filters.' : 'No competitors have been entered yet.'}</div> :
        <div className="tournamentCompetitors__tableWrap"><table><thead><tr><th>Competitor</th><th>School</th>{visibleColumns.age && <th>Age</th>}{visibleColumns.gender && <th>Gender</th>}{visibleColumns.birthday && <th>Date of birth</th>}{visibleColumns.events && <th>Events</th>}<th>Number</th></tr></thead>
          <tbody>{visibleItems.map(item => { const key = competitorKey(item); const changed = valueKey(item.quantumPlayerNumber) !== valueKey(this.state.originalNumbers[key]); return <tr key={key} className={changed ? 'is-changed' : ''}>
            <td><strong>{item.lastName}, {item.firstName}</strong></td><td>{item.schoolName}</td>
            {visibleColumns.age && <td>{displayAge(item)}</td>}{visibleColumns.gender && <td><span className={`tournamentCompetitors__gender is-${String(item.gender || '').toLowerCase()}`}>{displayGender(item.gender)}</span></td>}
            {visibleColumns.birthday && <td>{item.birthday && moment(item.birthday).isValid() ? moment(item.birthday).format('D MMM YYYY') : '—'}</td>}
            {visibleColumns.events && <td><div className="tournamentCompetitors__events">{(item.events || []).map(event => <span key={event.id}>{typeof event.quantumEventNumber === 'number' && <b>#{event.quantumEventNumber}</b>}{eventName(event)}</span>)}</div></td>}
            <td><input className="tournamentCompetitors__number" aria-label={`Competitor number for ${item.firstName} ${item.lastName}`} type="number" min="1" value={item.quantumPlayerNumber == null ? '' : item.quantumPlayerNumber} onChange={event => this.changeNumber(key, event.target.value)} />{changed && <small>Unsaved</small>}</td>
          </tr>; })}</tbody></table></div>}
    </section>;
  }
}
